import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "readTimelineIdsByRevision";
const UnreadRevisionesContext = createContext(null);

export function UnreadRevisionesProvider({ children }) {
  const [unreadByRevision, setUnreadByRevision] = useState({});
  const [readTimelineIdsByRevision, setReadTimelineIdsByRevision] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(readTimelineIdsByRevision));
    } catch (_) {}
  }, [readTimelineIdsByRevision]);

  const totalUnreadRevisiones = useMemo(
    () => Object.values(unreadByRevision).reduce((acc, n) => acc + (Number(n) || 0), 0),
    [unreadByRevision]
  );

  const setBulkUnreadByRevision = useCallback((updates) => {
    if (!updates || typeof updates !== "object") return;
    setUnreadByRevision((prev) => ({ ...prev, ...updates }));
  }, []);

  const markRevisionTimelineRead = useCallback((revisionId, timelineIds) => {
    if (!revisionId || !Array.isArray(timelineIds) || timelineIds.length === 0) return;
    setReadTimelineIdsByRevision((prev) => {
      const current = { ...(prev[revisionId] || {}) };
      let changed = false;
      timelineIds.forEach((tid) => {
        if (tid && !current[tid]) {
          current[tid] = true;
          changed = true;
        }
      });
      if (!changed) return prev;
      return { ...prev, [revisionId]: current };
    });
    setUnreadByRevision((prev) => ({ ...prev, [revisionId]: 0 }));
  }, []);

  /** Marca una respuesta de cliente como leída (persiste). El contador lo recalcula quien tenga el timeline (p. ej. detalle de revisión). */
  const setRevisionTimelineEventRead = useCallback((revisionId, eventId) => {
    if (!revisionId || !eventId) return;
    setReadTimelineIdsByRevision((prev) => {
      const byRev = prev[revisionId] || {};
      if (byRev[eventId]) return prev;
      return { ...prev, [revisionId]: { ...byRev, [eventId]: true } };
    });
  }, []);

  const isRevisionTimelineEventRead = useCallback(
    (revisionId, eventId) => Boolean(readTimelineIdsByRevision[revisionId]?.[eventId]),
    [readTimelineIdsByRevision]
  );

  return (
    <UnreadRevisionesContext.Provider
      value={{
        unreadByRevision,
        totalUnreadRevisiones,
        readTimelineIdsByRevision,
        setBulkUnreadByRevision,
        markRevisionTimelineRead,
        setRevisionTimelineEventRead,
        isRevisionTimelineEventRead,
      }}
    >
      {children}
    </UnreadRevisionesContext.Provider>
  );
}

const defaultCtx = {
  unreadByRevision: {},
  totalUnreadRevisiones: 0,
  readTimelineIdsByRevision: {},
  setBulkUnreadByRevision: () => {},
  markRevisionTimelineRead: () => {},
  setRevisionTimelineEventRead: () => {},
  isRevisionTimelineEventRead: () => false,
};

export function useUnreadRevisiones() {
  return useContext(UnreadRevisionesContext) || defaultCtx;
}
